export const translations = {
  en: {
    nav: {
      features: "Features",
      howItWorks: "How it Works",
      download: "Download",
    },
    hero: {
      title: "Where Timelines Intersect",
      subtitle: "Match through shared pasts and future dreams. Not just faces.",
      cta: "Download App",
      tagline: "The End of Swiping",
      translationBadge: "60ms Live AI Translation",
    },
    features: {
      title: "The Future of Connection",
      subtitle: "Say goodbye to swiping, say hello to depth. Connect through shared stories, not shallow swipes.",
      card1: {
        title: "Story-Based Matching",
        desc: "Connect through shared life experiences (Posts) and future dreams (Moments). Deeper conversations start here.",
      },
      card2: {
        title: "60ms Live Translation",
        desc: "Instant Global Conversation: Speak with anyone, anywhere, in your own language, in real-time with ultra-low latency AI.",
      },
      card3: {
        title: "Controlled Messaging",
        desc: "Match → Message Request → Approval. Quality over quantity. Your inbox, your rules.",
      },
      phone1Title: "Story-Based Profiles",
      phone1Desc: "Your Posts reveal your journey",
      phone2Title: "Match Through Experiences",
      phone2Desc: "Find people who share your interests",
      phone3Title: "Discover Moments",
      phone3Desc: "See future plans from people around you",
      ctaTitle: "Ready to find your timeline match?",
      ctaDesc: "Join thousands connecting through shared experiences.",
      ctaButton: "Get Started Free",
    },
    faq: {
      title: "Frequently Asked Questions",
      q1: "How is Joinzy different from other dating apps?",
      a1: "Unlike swipe-based apps, Joinzy matches you based on shared experiences (Posts) and future plans (Moments). It's about meaningful connections, not surface-level judgments. Your first step isn't liking a photo, it's becoming part of a life story.",
      q2: "What are Posts and Moments?",
      a2: "Posts (Traces of the Past): Share posts that reflect who you are, what you enjoy, and your experiences. A visit to an art gallery, a book you read, or a travel memory... Moments (Future Plans): Make instant 'tweet-style' announcements like 'I want to go to this concert this weekend'. Moments create matches focused on 'what you want to do'.",
      q3: "How does the messaging system work?",
      a3: "Your inbox is a showcase, not chaos. Approval-Based Start: When you match through a Post or Moment, messages don't flood in. You send a 'Message Request'. Message Rights: Each user has limited message initiation rights. This creates a 'quality filter' where every message is valuable.",
      q4: "How do video/audio calls work?",
      a4: "After matching and approval, Joinzy offers you the most advanced digital meeting room. Seamless Connection: Reduce distances to zero with high-quality audio and video calling. 60ms Live AI Translation (Premium): Language is a bridge, not a barrier.",
      q5: "Why Joinzy?",
      a5: "Deep, Not Surface: Match with your ideas and plans, not just your face. Your Time is Valuable: Escape profiles that 'message everyone' with limited message rights. A Global Network: Respond to a Moment on the other side of the world via the Matrix map.",
      stillHaveQuestions: "Still have questions?",
      contactUs: "Contact Us",
    },
    footer: {
      privacy: "Privacy Policy",
      terms: "Terms & Conditions",
      contact: "Contact",
      copyright: "© 2025 Alceylan Enterprises. All rights reserved.",
      legal: "Legal",
      tagline: "Connecting people through shared experiences and future dreams. The matrix of meaningful connections.",
      subscription: "Subscription",
      language: "Language",
      madeWith: "Made with the Matrix",
    },
    bubbles: {
      post1: "Visited Tokyo Tower last summer",
      post2: "Concert at Madison Square",
      moment1: "Planning Bali trip 2025",
      moment2: "Looking for hiking partners",
      post3: "Met at coffee shop downtown",
      moment3: "New Year's Eve party plans",
    },
    // Legal Page Translations
    legal: {
      title: "Legal & Privacy",
      languageDisclaimer: "The English version of this document is the legally binding version. Translations are provided for convenience only.",
      footer: "All rights reserved.",
      lastUpdated: "Last Updated",
      ageRestriction: {
        title: "Age Restriction (18+)",
        intro: "Joinzy is designed exclusively for adults. By using our services, you confirm that you meet the minimum age requirement.",
        warning: "We do not knowingly collect data from anyone under 18 years of age. Accounts found violating this policy will be terminated immediately without refund.",
        point1: "You must be at least 18 years old to create an account",
        point2: "Age verification may be required at any time",
        point3: "Providing false age information is a violation of our Terms of Service",
      },
      locationData: {
        title: "Location & Data Tracking",
        intro: "To provide our matching services and features, Joinzy collects and processes certain location and communication data.",
        geolocationTitle: "Precise Geolocation",
        geolocationDesc: "We use precise geolocation data to enable matching based on proximity and to power our 'Matrix World Map' feature. This allows you to discover and connect with users in your area or around the world.",
        videoAudioTitle: "Live Video/Audio Data",
        videoAudioDesc: "For our 60ms AI Translation service, live video and audio streams are processed in real-time to provide instant translation during calls.",
        privacyNote: "Privacy Guarantee: Live video/audio data is processed in real-time but is NEVER stored or recorded. Your conversations remain private.",
        matrixTitle: "Matrix World Map",
        matrixDesc: "The Matrix World Map displays anonymized location data to show global connectivity. Your exact location is never shown to other users without your explicit consent.",
      },
      payments: {
        title: "Payments & Refund Policy",
        intro: "All purchases within Joinzy are subject to our payment and refund policies as outlined below.",
        digitalGoods: "Non-Tangible Digital Goods",
        digitalGoodsDesc: "All purchases including Subscriptions, Message Credits, Premium Features, and other in-app purchases are classified as 'Non-Tangible Instant Digital Goods' that are delivered immediately upon purchase.",
        noRefund: "No Refund Policy",
        noRefundDesc: "All sales are final. No refunds will be issued once the digital content is accessed or credits are used. By completing a purchase, you acknowledge that you are waiving your right to a 'cooling-off' period.",
        refundRequest: "Refund Requests",
        refundRequestDesc: "If you believe you are entitled to a refund due to technical issues or billing errors, refund requests must be directed to Apple App Store or Google Play Store as per their respective terms and conditions.",
      },
      safety: {
        title: "Safety & Conduct",
        intro: "Joinzy is committed to providing a safe and respectful environment for all users.",
        messageRequest: "Message Request System",
        messageRequestDesc: "Users must approve Message Requests before conversations can begin. This system ensures that you have control over who can contact you and helps prevent unwanted messages.",
        zeroTolerance: "Zero Tolerance Policy",
        harassment: "Harassment, bullying, or threatening behavior",
        hateSpeech: "Hate speech, discrimination, or offensive content",
        illegalContent: "Illegal content or activities",
        impersonation: "Impersonation or fake profiles",
        spam: "Spam, scams, or commercial solicitation",
        consequences: "Violations will result in immediate account termination without refund and may be reported to relevant authorities.",
      },
      australian: {
        title: "Australian Legal Compliance",
        intro: "Alceylan Enterprises operates in compliance with Australian law and international privacy regulations.",
        privacyAct: "Australian Privacy Act 1988",
        privacyActDesc: "We comply with the Australian Privacy Act 1988 and the Australian Privacy Principles (APPs). This includes how we collect, use, disclose, and store your personal information.",
        apps: "Australian Privacy Principles (APPs)",
        appsDesc: "As required by the APPs, we maintain transparency in our data practices, provide access to your personal information upon request, and implement appropriate security measures to protect your data.",
        gdpr: "GDPR Compliance",
        gdprDesc: "For users in the European Economic Area, we also comply with the General Data Protection Regulation (GDPR), including rights to access, rectification, erasure, and data portability.",
      },
      contact: {
        title: "Contact Us",
        intro: "For any questions regarding our legal policies, privacy practices, or data-related inquiries, please contact us.",
        emailLabel: "Data Protection Inquiries",
        responseTime: "We aim to respond to all inquiries within 30 business days as required by applicable privacy laws.",
      },
    },
    // Cookie Consent Translations
    cookies: {
      title: "We Value Your Privacy",
      description: "We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking 'Accept All', you consent to our use of cookies.",
      acceptAll: "Accept All",
      rejectAll: "Reject All",
      customize: "Customize",
      settingsTitle: "Cookie Preferences",
      savePreferences: "Save Preferences",
      necessary: "Necessary Cookies",
      necessaryDesc: "These cookies are required for the website to function and cannot be disabled.",
      analytics: "Analytics Cookies",
      analyticsDesc: "Help us understand how visitors interact with our website by collecting anonymous information.",
      marketing: "Marketing Cookies",
      marketingDesc: "Used to track visitors across websites to display relevant advertisements.",
      personalization: "Personalization Cookies",
      personalizationDesc: "Allow the website to remember choices you make and provide enhanced features.",
    },
    subscription: {
      badge: "In-App Subscription Plans",
      heroTitle: "Find Your Perfect",
      heroPlan: "Plan",
      heroDesc: "From free to unlimited — choose the plan that fits how you connect. All purchases are made in the Joinzy mobile app.",
      billingNote: "Available billing periods — pricing shown in the app based on your region",
      mostPopular: "Most Popular",
      purchaseInApp: "Purchase in the app",
      comparisonTitle: "Plan Comparison",
      comparisonDesc: "See exactly what's included in each plan",
      featureLabel: "Feature",
      addOnTitle: "Add-On Packs",
      addOnDesc: "One-time purchases — no subscription required",
      partial: "Partial",
      ctaTitle: "Start for free, upgrade anytime",
      ctaDesc: "Download the Joinzy app and start connecting. Upgrade to Plus, Gold, or Premium directly in the app via App Store or Google Play.",
      ctaNote: "Subscriptions auto-renew. Cancel anytime from your App Store or Google Play account settings.",
      downloadApp: "Download the App",
      haveQuestions: "Have questions?",
      monthly: "Monthly",
      months3: "3 Months",
      months6: "6 Months",
      months12: "12 Months",
      popularTag: "Popular",
      bestValueTag: "Best Value",
      freeDesc: "Try Joinzy for free with basic limits.",
      plusDesc: "More chats, fewer ads, stay active daily.",
      goldDesc: "Voice & video calls and full ad-free experience.",
      premiumDesc: "Unlimited video calls with live translation.",
      feat5msg: "5 messages / day",
      feat15msg: "15 messages / day",
      feat30msg: "30 messages / day",
      featUnlimitedMsg: "Unlimited messages",
      feat3posts: "3 posts / day",
      feat7posts: "7 posts / day",
      feat15posts: "15 posts / day",
      featUnlimitedPosts: "Unlimited posts",
      featPush: "Push notifications",
      featHashtag: "Hashtag matching",
      featMatrix: "Matrix World Map",
      featVoice: "Voice calls",
      featVideo: "Video calls",
      featTranslation: "Live translation",
      featReducedAds: "Reduced ads",
      featAdFree: "Ad-free experience",
      featPacks: "Message & call packs",
      featCompleteAdFree: "Completely ad-free",
      featExtendedMatch: "Extended match window",
      msgPackTitle: "Message Packs",
      msgPackDesc: "Need more messages today? Buy a one-time pack without changing your plan.",
      postPackTitle: "Post Packs",
      postPackDesc: "Share more photos and videos today with a one-time post pack.",
      callPackTitle: "Call Packs",
      callPackDesc: "Get extra call time instantly, available for Plus and above.",
      packSmallMsg: "Small — 2 messages",
      packMediumMsg: "Medium — 5 messages",
      packLargeMsg: "Large — 10 messages",
      packSmallPost: "Small — 2 posts",
      packMediumPost: "Medium — 5 posts",
      packLargePost: "Large — 10 posts",
      packSmallCall: "Small — 5 min",
      packMediumCall: "Medium — 15 min",
      packLargeCall: "Large — 30 min",
      packXLCall: "Extra Large — Unlimited (today)",
      cmpDailyMsg: "Daily Messages",
      cmpDailyPosts: "Daily Posts",
      cmpPush: "Push Notifications",
      cmpVoice: "Voice Calls",
      cmpVideo: "Video Calls",
      cmpTranslation: "Live Translation",
      cmpAdFree: "Ad-Free",
      cmpMatchWindow: "Extended Match Window",
      cmpPacks: "Consumable Packs",
    },
    contactModal: {
      title: "Contact Us",
      subtitle: "Alceylan Enterprises",
      nameLabel: "Name",
      emailLabel: "Email",
      subjectLabel: "Subject",
      messageLabel: "Message",
      namePlaceholder: "Your name",
      emailPlaceholder: "your@email.com",
      subjectPlaceholder: "What's this about?",
      messagePlaceholder: "Write your message here...",
      sendButton: "Send Message",
      sendingButton: "Sending...",
      closeButton: "Close",
      successTitle: "Message Sent!",
      successDesc: "We've received your message and will get back to you as soon as possible.",
      errorMessage: "Something went wrong. Please try again or email us directly.",
      directEmail: "Or email us directly at",
    },
  },
  es: {
    nav: {
      features: "Características",
      howItWorks: "Cómo Funciona",
      download: "Descargar",
    },
    hero: {
      title: "Donde las Líneas del Tiempo se Cruzan",
      subtitle: "Conecta a través de pasados compartidos y sueños futuros. No solo caras.",
      cta: "Descargar App",
      tagline: "El Fin del Deslizamiento",
      translationBadge: "Traducción AI en Vivo 60ms",
    },
    features: {
      title: "El Futuro de la Conexión",
      subtitle: "Di adiós al deslizamiento, di hola a la profundidad. Conecta a través de historias compartidas.",
      card1: { title: "Matching Basado en Historias", desc: "Conecta a través de experiencias de vida compartidas (Posts) y sueños futuros (Momentos). Las conversaciones más profundas empiezan aquí." },
      card2: { title: "Traducción en Vivo 60ms", desc: "Conversación Global Instantánea: Habla con cualquiera, en cualquier lugar, en tu propio idioma, en tiempo real con IA de ultra-baja latencia." },
      card3: { title: "Mensajería Controlada", desc: "Match → Solicitud de Mensaje → Aprobación. Calidad sobre cantidad. Tu bandeja de entrada, tus reglas." },
      phone1Title: "Perfiles Basados en Historias",
      phone1Desc: "Tus Posts revelan tu viaje",
      phone2Title: "Conecta a Través de Experiencias",
      phone2Desc: "Encuentra personas que comparten tus intereses",
      phone3Title: "Descubre Momentos",
      phone3Desc: "Ve planes futuros de personas a tu alrededor",
      ctaTitle: "¿Listo para encontrar tu match de línea temporal?",
      ctaDesc: "Únete a miles conectando a través de experiencias compartidas.",
      ctaButton: "Comenzar Gratis",
    },
    faq: {
      title: "Preguntas Frecuentes",
      q1: "¿Cómo es Joinzy diferente de otras apps de citas?",
      a1: "A diferencia de las apps basadas en deslizar, Joinzy te conecta basándose en experiencias compartidas y planes futuros.",
      q2: "¿Qué son los Posts y los Momentos?",
      a2: "Posts son tus experiencias pasadas. Momentos son tus planes futuros.",
      q3: "¿Cómo funciona el sistema de mensajería?",
      a3: "Tu bandeja de entrada es un escaparate, no un caos.",
      q4: "¿Cómo funcionan las videollamadas?",
      a4: "Joinzy te ofrece la sala de reuniones digital más avanzada.",
      q5: "¿Por qué Joinzy?",
      a5: "Profundo, No Superficial: Conecta con tus ideas y planes.",
      stillHaveQuestions: "¿Todavía tienes preguntas?",
      contactUs: "Contáctanos",
    },
    footer: {
      privacy: "Política de Privacidad",
      terms: "Términos y Condiciones",
      contact: "Contacto",
      copyright: "© 2025 Alceylan Enterprises. Todos los derechos reservados.",
      legal: "Legal",
      tagline: "Conectando personas a través de experiencias compartidas y sueños futuros. La matriz de conexiones significativas.",
      subscription: "Suscripción",
      language: "Idioma",
      madeWith: "Hecho con la Matrix",
    },
    bubbles: {
      post1: "Visité la Torre de Tokio",
      post2: "Concierto en Madison Square",
      moment1: "Planeando viaje a Bali 2025",
      moment2: "Buscando compañeros de senderismo",
      post3: "Nos conocimos en la cafetería",
      moment3: "Planes para Año Nuevo",
    },
    legal: {
      title: "Legal y Privacidad",
      languageDisclaimer: "La versión en inglés de este documento es la versión legalmente vinculante. Las traducciones se proporcionan solo por conveniencia.",
      footer: "Todos los derechos reservados.",
      lastUpdated: "Última actualización",
      ageRestriction: {
        title: "Restricción de Edad (18+)",
        intro: "Joinzy está diseñado exclusivamente para adultos.",
        warning: "No recopilamos datos de menores de 18 años. Las cuentas que violen esta política serán terminadas inmediatamente sin reembolso.",
        point1: "Debes tener al menos 18 años para crear una cuenta",
        point2: "Se puede requerir verificación de edad en cualquier momento",
        point3: "Proporcionar información de edad falsa es una violación",
      },
      locationData: {
        title: "Ubicación y Seguimiento de Datos",
        intro: "Para proporcionar nuestros servicios, Joinzy recopila y procesa ciertos datos de ubicación y comunicación.",
        geolocationTitle: "Geolocalización Precisa",
        geolocationDesc: "Utilizamos datos de geolocalización precisa para permitir el emparejamiento basado en proximidad.",
        videoAudioTitle: "Datos de Video/Audio en Vivo",
        videoAudioDesc: "Para nuestro servicio de traducción AI de 60ms, los flujos de video y audio se procesan en tiempo real.",
        privacyNote: "Garantía de Privacidad: Los datos de video/audio NUNCA se almacenan ni graban.",
        matrixTitle: "Mapa Matrix Mundial",
        matrixDesc: "El Mapa Matrix Mundial muestra datos de ubicación anonimizados.",
      },
      payments: {
        title: "Pagos y Política de Reembolso",
        intro: "Todas las compras dentro de Joinzy están sujetas a nuestras políticas.",
        digitalGoods: "Bienes Digitales No Tangibles",
        digitalGoodsDesc: "Todas las compras se clasifican como 'Bienes Digitales Instantáneos No Tangibles'.",
        noRefund: "Política de No Reembolso",
        noRefundDesc: "Todas las ventas son finales. No se emitirán reembolsos.",
        refundRequest: "Solicitudes de Reembolso",
        refundRequestDesc: "Las solicitudes de reembolso deben dirigirse a Apple App Store o Google Play Store.",
      },
      safety: {
        title: "Seguridad y Conducta",
        intro: "Joinzy está comprometido a proporcionar un ambiente seguro y respetuoso.",
        messageRequest: "Sistema de Solicitud de Mensajes",
        messageRequestDesc: "Los usuarios deben aprobar las Solicitudes de Mensajes antes de que puedan comenzar las conversaciones.",
        zeroTolerance: "Política de Tolerancia Cero",
        harassment: "Acoso, intimidación o comportamiento amenazante",
        hateSpeech: "Discurso de odio, discriminación o contenido ofensivo",
        illegalContent: "Contenido o actividades ilegales",
        impersonation: "Suplantación o perfiles falsos",
        spam: "Spam, estafas o solicitud comercial",
        consequences: "Las violaciones resultarán en la terminación inmediata de la cuenta sin reembolso.",
      },
      australian: {
        title: "Cumplimiento Legal Australiano",
        intro: "Alceylan Enterprises opera en cumplimiento con la ley australiana.",
        privacyAct: "Ley de Privacidad de Australia 1988",
        privacyActDesc: "Cumplimos con la Ley de Privacidad de Australia 1988.",
        apps: "Principios de Privacidad Australianos (APPs)",
        appsDesc: "Mantenemos transparencia en nuestras prácticas de datos.",
        gdpr: "Cumplimiento del GDPR",
        gdprDesc: "Para usuarios en el EEE, también cumplimos con el GDPR.",
      },
      contact: {
        title: "Contáctenos",
        intro: "Para cualquier pregunta sobre nuestras políticas legales, contáctenos.",
        emailLabel: "Consultas de Protección de Datos",
        responseTime: "Respondemos a todas las consultas dentro de 30 días hábiles.",
      },
    },
    cookies: {
      title: "Valoramos Tu Privacidad",
      description: "Utilizamos cookies para mejorar tu experiencia de navegación.",
      acceptAll: "Aceptar Todo",
      rejectAll: "Rechazar Todo",
      customize: "Personalizar",
      settingsTitle: "Preferencias de Cookies",
      savePreferences: "Guardar Preferencias",
      necessary: "Cookies Necesarias",
      necessaryDesc: "Estas cookies son necesarias para el funcionamiento del sitio.",
      analytics: "Cookies de Análisis",
      analyticsDesc: "Nos ayudan a entender cómo interactúan los visitantes con nuestro sitio.",
      marketing: "Cookies de Marketing",
      marketingDesc: "Se utilizan para mostrar anuncios relevantes.",
      personalization: "Cookies de Personalización",
      personalizationDesc: "Permiten que el sitio recuerde tus preferencias.",
    },
    subscription: {
      badge: "Planes de Suscripción en la App",
      heroTitle: "Encuentra tu Plan",
      heroPlan: "Perfecto",
      heroDesc: "De gratuito a ilimitado — elige el plan que se adapta a tu forma de conectar. Todas las compras se realizan en la app móvil de Joinzy.",
      billingNote: "Períodos de facturación disponibles — precios según tu región en la app",
      mostPopular: "Más Popular",
      purchaseInApp: "Comprar en la app",
      comparisonTitle: "Comparación de Planes",
      comparisonDesc: "Ve exactamente qué está incluido en cada plan",
      featureLabel: "Función",
      addOnTitle: "Paquetes Adicionales",
      addOnDesc: "Compras únicas — sin suscripción requerida",
      partial: "Parcial",
      ctaTitle: "Empieza gratis, mejora cuando quieras",
      ctaDesc: "Descarga Joinzy y empieza a conectar. Mejora a Plus, Gold o Premium directamente en la app.",
      ctaNote: "Las suscripciones se renuevan automáticamente. Cancela en cualquier momento desde App Store o Google Play.",
      downloadApp: "Descargar la App",
      haveQuestions: "¿Tienes preguntas?",
      monthly: "Mensual",
      months3: "3 Meses",
      months6: "6 Meses",
      months12: "12 Meses",
      popularTag: "Popular",
      bestValueTag: "Mejor Valor",
      freeDesc: "Prueba Joinzy gratis con límites básicos.",
      plusDesc: "Más chats, menos anuncios, mantente activo cada día.",
      goldDesc: "Llamadas de voz y video con experiencia sin anuncios.",
      premiumDesc: "Videollamadas ilimitadas con traducción en vivo.",
      feat5msg: "5 mensajes / día",
      feat15msg: "15 mensajes / día",
      feat30msg: "30 mensajes / día",
      featUnlimitedMsg: "Mensajes ilimitados",
      feat3posts: "3 publicaciones / día",
      feat7posts: "7 publicaciones / día",
      feat15posts: "15 publicaciones / día",
      featUnlimitedPosts: "Publicaciones ilimitadas",
      featPush: "Notificaciones push",
      featHashtag: "Coincidencia por hashtags",
      featMatrix: "Mapa Matrix Mundial",
      featVoice: "Llamadas de voz",
      featVideo: "Videollamadas",
      featTranslation: "Traducción en vivo",
      featReducedAds: "Anuncios reducidos",
      featAdFree: "Sin anuncios",
      featPacks: "Paquetes de mensajes y llamadas",
      featCompleteAdFree: "Completamente sin anuncios",
      featExtendedMatch: "Ventana de coincidencia extendida",
      msgPackTitle: "Paquetes de Mensajes",
      msgPackDesc: "¿Necesitas más mensajes hoy? Compra un paquete único sin cambiar tu plan.",
      postPackTitle: "Paquetes de Publicaciones",
      postPackDesc: "Comparte más fotos y videos hoy con un paquete único.",
      callPackTitle: "Paquetes de Llamadas",
      callPackDesc: "Obtén tiempo extra de llamada al instante, disponible para Plus y superior.",
      packSmallMsg: "Pequeño — 2 mensajes",
      packMediumMsg: "Mediano — 5 mensajes",
      packLargeMsg: "Grande — 10 mensajes",
      packSmallPost: "Pequeño — 2 publicaciones",
      packMediumPost: "Mediano — 5 publicaciones",
      packLargePost: "Grande — 10 publicaciones",
      packSmallCall: "Pequeño — 5 min",
      packMediumCall: "Mediano — 15 min",
      packLargeCall: "Grande — 30 min",
      packXLCall: "Extra Grande — Ilimitado (hoy)",
      cmpDailyMsg: "Mensajes Diarios",
      cmpDailyPosts: "Publicaciones Diarias",
      cmpPush: "Notificaciones Push",
      cmpVoice: "Llamadas de Voz",
      cmpVideo: "Videollamadas",
      cmpTranslation: "Traducción en Vivo",
      cmpAdFree: "Sin Anuncios",
      cmpMatchWindow: "Ventana de Coincidencia Extendida",
      cmpPacks: "Paquetes Consumibles",
    },
    contactModal: {
      title: "Contáctanos",
      subtitle: "Alceylan Enterprises",
      nameLabel: "Nombre",
      emailLabel: "Correo electrónico",
      subjectLabel: "Asunto",
      messageLabel: "Mensaje",
      namePlaceholder: "Tu nombre",
      emailPlaceholder: "tu@correo.com",
      subjectPlaceholder: "¿De qué se trata?",
      messagePlaceholder: "Escribe tu mensaje aquí...",
      sendButton: "Enviar Mensaje",
      sendingButton: "Enviando...",
      closeButton: "Cerrar",
      successTitle: "¡Mensaje Enviado!",
      successDesc: "Hemos recibido tu mensaje y te responderemos lo antes posible.",
      errorMessage: "Algo salió mal. Por favor intenta de nuevo o escríbenos directamente.",
      directEmail: "O escríbenos directamente a",
    },
  },
  it: {
    nav: { features: "Caratteristiche", howItWorks: "Come Funziona", download: "Scarica" },
    hero: { title: "Dove le Linee Temporali si Incrociano", subtitle: "Connettiti attraverso passati condivisi e sogni futuri.", cta: "Scarica l'App", tagline: "La Fine dello Swipe", translationBadge: "Traduzione AI Live 60ms" },
    features: {
      title: "Il Futuro della Connessione",
      subtitle: "Di' addio allo swipe, di' ciao alla profondità.",
      card1: { title: "Matching Basato sulle Storie", desc: "Connettiti attraverso esperienze di vita condivise (Post) e sogni futuri (Momenti). Le conversazioni più profonde iniziano qui." },
      card2: { title: "Traduzione in Tempo Reale 60ms", desc: "Conversazione Globale Istantanea: Parla con chiunque, ovunque, nella tua lingua, in tempo reale con IA ultra-bassa latenza." },
      card3: { title: "Messaggistica Controllata", desc: "Match → Richiesta Messaggio → Approvazione. Qualità sulla quantità. La tua posta in arrivo, le tue regole." },
      phone1Title: "Profili Basati sulle Storie",
      phone1Desc: "I tuoi Post rivelano il tuo viaggio",
      phone2Title: "Connettiti Attraverso le Esperienze",
      phone2Desc: "Trova persone che condividono i tuoi interessi",
      phone3Title: "Scopri i Momenti",
      phone3Desc: "Guarda i piani futuri delle persone intorno a te",
      ctaTitle: "Pronto a trovare il tuo match temporale?",
      ctaDesc: "Unisciti a migliaia che si connettono attraverso esperienze condivise.",
      ctaButton: "Inizia Gratis",
    },
    faq: { title: "Domande Frequenti", q1: "Come è diverso Joinzy?", a1: "Joinzy ti connette in base a esperienze condivise.", q2: "Cosa sono Post e Momenti?", a2: "Post sono esperienze passate. Momenti sono piani futuri.", q3: "Come funziona la messaggistica?", a3: "La tua casella è una vetrina.", q4: "Come funzionano le videochiamate?", a4: "Joinzy offre la sala riunioni più avanzata.", q5: "Perché Joinzy?", a5: "Profondo, Non Superficiale.", stillHaveQuestions: "Hai ancora domande?", contactUs: "Contattaci" },
    footer: { privacy: "Privacy", terms: "Termini", contact: "Contatto", copyright: "© 2025 Alceylan Enterprises.", legal: "Legale", tagline: "Connettere persone attraverso esperienze condivise e sogni futuri. La matrice di connessioni significative.", subscription: "Abbonamento", language: "Lingua", madeWith: "Fatto con la Matrix" },
    bubbles: { post1: "Torre di Tokyo", post2: "Concerto Madison Square", moment1: "Viaggio Bali 2025", moment2: "Cerco escursionisti", post3: "Incontro al bar", moment3: "Capodanno" },
    legal: {
      title: "Legale e Privacy", languageDisclaimer: "La versione inglese è legalmente vincolante.", footer: "Tutti i diritti riservati.", lastUpdated: "Ultimo aggiornamento",
      ageRestriction: { title: "Restrizione Età (18+)", intro: "Joinzy è per adulti.", warning: "Non raccogliamo dati da minori di 18 anni.", point1: "Devi avere almeno 18 anni", point2: "Verifica età può essere richiesta", point3: "Età falsa è una violazione" },
      locationData: { title: "Posizione e Dati", intro: "Joinzy raccoglie dati di posizione.", geolocationTitle: "Geolocalizzazione", geolocationDesc: "Usiamo dati di posizione precisi.", videoAudioTitle: "Video/Audio Live", videoAudioDesc: "Elaborati in tempo reale per traduzione AI.", privacyNote: "I dati NON vengono mai memorizzati.", matrixTitle: "Mappa Matrix", matrixDesc: "Mostra dati di posizione anonimi." },
      payments: { title: "Pagamenti e Rimborsi", intro: "Tutti gli acquisti sono soggetti alle nostre politiche.", digitalGoods: "Beni Digitali", digitalGoodsDesc: "Tutti gli acquisti sono beni digitali istantanei.", noRefund: "Nessun Rimborso", noRefundDesc: "Tutte le vendite sono finali.", refundRequest: "Richieste Rimborso", refundRequestDesc: "Dirigere a App Store o Play Store." },
      safety: { title: "Sicurezza", intro: "Ambiente sicuro e rispettoso.", messageRequest: "Sistema Richieste", messageRequestDesc: "Approvazione richiesta per messaggi.", zeroTolerance: "Tolleranza Zero", harassment: "Molestie", hateSpeech: "Odio", illegalContent: "Contenuti illegali", impersonation: "Impersonazione", spam: "Spam", consequences: "Terminazione account immediata." },
      australian: { title: "Conformità Australiana", intro: "Operiamo in conformità.", privacyAct: "Privacy Act 1988", privacyActDesc: "Rispettiamo la legge australiana.", apps: "APPs", appsDesc: "Trasparenza nei dati.", gdpr: "GDPR", gdprDesc: "Conformità per utenti EEA." },
      contact: { title: "Contattaci", intro: "Per domande, contattaci.", emailLabel: "Richieste Privacy", responseTime: "Rispondiamo entro 30 giorni." },
    },
    cookies: { title: "Privacy", description: "Usiamo cookie per migliorare l'esperienza.", acceptAll: "Accetta Tutto", rejectAll: "Rifiuta Tutto", customize: "Personalizza", settingsTitle: "Preferenze Cookie", savePreferences: "Salva", necessary: "Necessari", necessaryDesc: "Richiesti per funzionamento.", analytics: "Analisi", analyticsDesc: "Capire interazioni.", marketing: "Marketing", marketingDesc: "Annunci pertinenti.", personalization: "Personalizzazione", personalizationDesc: "Ricordare preferenze." },
    subscription: {
      badge: "Piani di Abbonamento In-App",
      heroTitle: "Trova il tuo Piano",
      heroPlan: "Perfetto",
      heroDesc: "Dal gratuito all'illimitato — scegli il piano adatto a come ti connetti. Tutti gli acquisti vengono effettuati nell'app mobile Joinzy.",
      billingNote: "Periodi di fatturazione disponibili — prezzi mostrati nell'app in base alla tua regione",
      mostPopular: "Più Popolare",
      purchaseInApp: "Acquista nell'app",
      comparisonTitle: "Confronto Piani",
      comparisonDesc: "Scopri esattamente cosa è incluso in ogni piano",
      featureLabel: "Funzione",
      addOnTitle: "Pacchetti Aggiuntivi",
      addOnDesc: "Acquisti una tantum — nessun abbonamento richiesto",
      partial: "Parziale",
      ctaTitle: "Inizia gratis, aggiorna quando vuoi",
      ctaDesc: "Scarica Joinzy e inizia a connetterti. Passa a Plus, Gold o Premium direttamente nell'app.",
      ctaNote: "Gli abbonamenti si rinnovano automaticamente. Cancella in qualsiasi momento dall'App Store o Google Play.",
      downloadApp: "Scarica l'App",
      haveQuestions: "Hai domande?",
      monthly: "Mensile",
      months3: "3 Mesi",
      months6: "6 Mesi",
      months12: "12 Mesi",
      popularTag: "Popolare",
      bestValueTag: "Miglior Valore",
      freeDesc: "Prova Joinzy gratuitamente con limiti base.",
      plusDesc: "Più chat, meno pubblicità, rimani attivo ogni giorno.",
      goldDesc: "Chiamate vocali e video con esperienza senza pubblicità.",
      premiumDesc: "Videochiamate illimitate con traduzione live.",
      feat5msg: "5 messaggi / giorno",
      feat15msg: "15 messaggi / giorno",
      feat30msg: "30 messaggi / giorno",
      featUnlimitedMsg: "Messaggi illimitati",
      feat3posts: "3 post / giorno",
      feat7posts: "7 post / giorno",
      feat15posts: "15 post / giorno",
      featUnlimitedPosts: "Post illimitati",
      featPush: "Notifiche push",
      featHashtag: "Corrispondenza hashtag",
      featMatrix: "Mappa Matrix Mondiale",
      featVoice: "Chiamate vocali",
      featVideo: "Videochiamate",
      featTranslation: "Traduzione live",
      featReducedAds: "Pubblicità ridotta",
      featAdFree: "Senza pubblicità",
      featPacks: "Pacchetti messaggi e chiamate",
      featCompleteAdFree: "Completamente senza pubblicità",
      featExtendedMatch: "Finestra di corrispondenza estesa",
      msgPackTitle: "Pacchetti Messaggi",
      msgPackDesc: "Hai bisogno di più messaggi oggi? Acquista un pacchetto una tantum senza cambiare piano.",
      postPackTitle: "Pacchetti Post",
      postPackDesc: "Condividi più foto e video oggi con un pacchetto una tantum.",
      callPackTitle: "Pacchetti Chiamate",
      callPackDesc: "Ottieni tempo extra di chiamata istantaneamente, disponibile per Plus e superiori.",
      packSmallMsg: "Piccolo — 2 messaggi",
      packMediumMsg: "Medio — 5 messaggi",
      packLargeMsg: "Grande — 10 messaggi",
      packSmallPost: "Piccolo — 2 post",
      packMediumPost: "Medio — 5 post",
      packLargePost: "Grande — 10 post",
      packSmallCall: "Piccolo — 5 min",
      packMediumCall: "Medio — 15 min",
      packLargeCall: "Grande — 30 min",
      packXLCall: "Extra Grande — Illimitato (oggi)",
      cmpDailyMsg: "Messaggi Giornalieri",
      cmpDailyPosts: "Post Giornalieri",
      cmpPush: "Notifiche Push",
      cmpVoice: "Chiamate Vocali",
      cmpVideo: "Videochiamate",
      cmpTranslation: "Traduzione Live",
      cmpAdFree: "Senza Pubblicità",
      cmpMatchWindow: "Finestra di Corrispondenza Estesa",
      cmpPacks: "Pacchetti Consumabili",
    },
    contactModal: {
      title: "Contattaci",
      subtitle: "Alceylan Enterprises",
      nameLabel: "Nome",
      emailLabel: "Email",
      subjectLabel: "Oggetto",
      messageLabel: "Messaggio",
      namePlaceholder: "Il tuo nome",
      emailPlaceholder: "tua@email.com",
      subjectPlaceholder: "Di cosa si tratta?",
      messagePlaceholder: "Scrivi il tuo messaggio qui...",
      sendButton: "Invia Messaggio",
      sendingButton: "Invio in corso...",
      closeButton: "Chiudi",
      successTitle: "Messaggio Inviato!",
      successDesc: "Abbiamo ricevuto il tuo messaggio e ti risponderemo il prima possibile.",
      errorMessage: "Qualcosa è andato storto. Riprova o scrivici direttamente.",
      directEmail: "Oppure scrivici direttamente a",
    },
  },
  de: {
    nav: { features: "Funktionen", howItWorks: "So funktioniert's", download: "Herunterladen" },
    hero: { title: "Wo sich Zeitlinien kreuzen", subtitle: "Verbinde dich durch geteilte Vergangenheit und Träume.", cta: "App herunterladen", tagline: "Das Ende des Swipens", translationBadge: "60ms Live-KI-Übersetzung" },
    features: {
      title: "Die Zukunft der Verbindung",
      subtitle: "Verabschiede dich vom Swipen, begrüße die Tiefe.",
      card1: { title: "Erfahrungsbasiertes Matching", desc: "Verbinde dich durch geteilte Lebenserfahrungen (Posts) und Zukunftsträume (Momente). Tiefere Gespräche beginnen hier." },
      card2: { title: "60ms Live-Übersetzung", desc: "Sofortige globale Kommunikation: Sprich mit jedem, überall, in deiner eigenen Sprache, in Echtzeit mit ultra-niedriger KI-Latenz." },
      card3: { title: "Kontrolliertes Messaging", desc: "Match → Nachrichtenanfrage → Genehmigung. Qualität vor Quantität. Dein Posteingang, deine Regeln." },
      phone1Title: "Erfahrungsbasierte Profile",
      phone1Desc: "Deine Posts enthüllen deine Reise",
      phone2Title: "Verbinde durch Erfahrungen",
      phone2Desc: "Finde Menschen, die deine Interessen teilen",
      phone3Title: "Momente entdecken",
      phone3Desc: "Sieh Zukunftspläne von Menschen um dich herum",
      ctaTitle: "Bereit, deinen Timeline-Match zu finden?",
      ctaDesc: "Schließe dich Tausenden an, die durch geteilte Erfahrungen verbunden sind.",
      ctaButton: "Kostenlos starten",
    },
    faq: { title: "Häufige Fragen", q1: "Wie ist Joinzy anders?", a1: "Joinzy verbindet basierend auf Erfahrungen.", q2: "Was sind Posts und Momente?", a2: "Posts sind Vergangenheit. Momente sind Zukunft.", q3: "Wie funktioniert Messaging?", a3: "Dein Posteingang ist ein Schaufenster.", q4: "Wie funktionieren Anrufe?", a4: "Joinzy bietet fortschrittliche Räume.", q5: "Warum Joinzy?", a5: "Tief, Nicht Oberflächlich.", stillHaveQuestions: "Noch Fragen?", contactUs: "Kontaktiere uns" },
    footer: { privacy: "Datenschutz", terms: "AGB", contact: "Kontakt", copyright: "© 2025 Alceylan Enterprises.", legal: "Rechtliches", tagline: "Menschen durch gemeinsame Erfahrungen und Zukunftsträume verbinden. Die Matrix bedeutungsvoller Verbindungen.", subscription: "Abonnement", language: "Sprache", madeWith: "Gemacht mit der Matrix" },
    bubbles: { post1: "Tokyo Tower besucht", post2: "Madison Square Konzert", moment1: "Bali Reise 2025", moment2: "Wanderpartner gesucht", post3: "Im Café getroffen", moment3: "Silvester Pläne" },
    legal: {
      title: "Rechtliches & Datenschutz", languageDisclaimer: "Die englische Version ist rechtlich bindend.", footer: "Alle Rechte vorbehalten.", lastUpdated: "Zuletzt aktualisiert",
      ageRestriction: { title: "Altersbeschränkung (18+)", intro: "Joinzy ist nur für Erwachsene.", warning: "Wir sammeln keine Daten von unter 18-Jährigen.", point1: "Mindestens 18 Jahre alt", point2: "Altersverifikation möglich", point3: "Falsches Alter ist ein Verstoß" },
      locationData: { title: "Standort & Daten", intro: "Joinzy sammelt Standortdaten.", geolocationTitle: "Präzise Geolokalisierung", geolocationDesc: "Für Matching und Matrix-Karte.", videoAudioTitle: "Live Video/Audio", videoAudioDesc: "Echtzeit-Verarbeitung für AI-Übersetzung.", privacyNote: "Daten werden NIEMALS gespeichert.", matrixTitle: "Matrix Weltkarte", matrixDesc: "Zeigt anonymisierte Standorte." },
      payments: { title: "Zahlungen & Rückerstattung", intro: "Alle Käufe unterliegen unseren Richtlinien.", digitalGoods: "Digitale Güter", digitalGoodsDesc: "Alle Käufe sind sofortige digitale Güter.", noRefund: "Keine Rückerstattung", noRefundDesc: "Alle Verkäufe sind endgültig.", refundRequest: "Rückerstattungsanfragen", refundRequestDesc: "An App Store oder Play Store richten." },
      safety: { title: "Sicherheit & Verhalten", intro: "Sichere und respektvolle Umgebung.", messageRequest: "Nachrichtenanfrage-System", messageRequestDesc: "Genehmigung erforderlich.", zeroTolerance: "Null-Toleranz", harassment: "Belästigung", hateSpeech: "Hassrede", illegalContent: "Illegale Inhalte", impersonation: "Identitätsbetrug", spam: "Spam", consequences: "Sofortige Kontosperrung." },
      australian: { title: "Australische Compliance", intro: "Wir befolgen australisches Recht.", privacyAct: "Privacy Act 1988", privacyActDesc: "Wir befolgen das australische Datenschutzgesetz.", apps: "APPs", appsDesc: "Transparenz bei Daten.", gdpr: "DSGVO", gdprDesc: "Compliance für EWR-Nutzer." },
      contact: { title: "Kontakt", intro: "Bei Fragen kontaktieren Sie uns.", emailLabel: "Datenschutzanfragen", responseTime: "Antwort innerhalb von 30 Tagen." },
    },
    cookies: { title: "Datenschutz", description: "Wir verwenden Cookies.", acceptAll: "Alle akzeptieren", rejectAll: "Alle ablehnen", customize: "Anpassen", settingsTitle: "Cookie-Einstellungen", savePreferences: "Speichern", necessary: "Notwendig", necessaryDesc: "Für Funktionalität erforderlich.", analytics: "Analyse", analyticsDesc: "Interaktionen verstehen.", marketing: "Marketing", marketingDesc: "Relevante Werbung.", personalization: "Personalisierung", personalizationDesc: "Einstellungen merken." },
    subscription: {
      badge: "In-App-Abonnementpläne",
      heroTitle: "Finde deinen perfekten",
      heroPlan: "Plan",
      heroDesc: "Von kostenlos bis unbegrenzt — wähle den Plan, der zu deiner Art der Verbindung passt. Alle Käufe werden in der Joinzy-App getätigt.",
      billingNote: "Verfügbare Abrechnungszeiträume — Preise in der App je nach Region",
      mostPopular: "Am Beliebtesten",
      purchaseInApp: "In der App kaufen",
      comparisonTitle: "Planvergleich",
      comparisonDesc: "Sieh genau, was in jedem Plan enthalten ist",
      featureLabel: "Funktion",
      addOnTitle: "Zusatzpakete",
      addOnDesc: "Einmalige Käufe — kein Abonnement erforderlich",
      partial: "Teilweise",
      ctaTitle: "Kostenlos starten, jederzeit upgraden",
      ctaDesc: "Lade Joinzy herunter und verbinde dich. Upgrade auf Plus, Gold oder Premium direkt in der App.",
      ctaNote: "Abonnements verlängern sich automatisch. Jederzeit über App Store oder Google Play kündigen.",
      downloadApp: "App herunterladen",
      haveQuestions: "Hast du Fragen?",
      monthly: "Monatlich",
      months3: "3 Monate",
      months6: "6 Monate",
      months12: "12 Monate",
      popularTag: "Beliebt",
      bestValueTag: "Bestes Preis-Leistungs-Verhältnis",
      freeDesc: "Probiere Joinzy kostenlos mit grundlegenden Limits.",
      plusDesc: "Mehr Chats, weniger Werbung, täglich aktiv bleiben.",
      goldDesc: "Sprach- und Videoanrufe mit werbefreier Erfahrung.",
      premiumDesc: "Unbegrenzte Videoanrufe mit Live-Übersetzung.",
      feat5msg: "5 Nachrichten / Tag",
      feat15msg: "15 Nachrichten / Tag",
      feat30msg: "30 Nachrichten / Tag",
      featUnlimitedMsg: "Unbegrenzte Nachrichten",
      feat3posts: "3 Beiträge / Tag",
      feat7posts: "7 Beiträge / Tag",
      feat15posts: "15 Beiträge / Tag",
      featUnlimitedPosts: "Unbegrenzte Beiträge",
      featPush: "Push-Benachrichtigungen",
      featHashtag: "Hashtag-Matching",
      featMatrix: "Matrix-Weltkarte",
      featVoice: "Sprachanrufe",
      featVideo: "Videoanrufe",
      featTranslation: "Live-Übersetzung",
      featReducedAds: "Reduzierte Werbung",
      featAdFree: "Werbefreie Erfahrung",
      featPacks: "Nachrichten- und Anrufpakete",
      featCompleteAdFree: "Komplett werbefrei",
      featExtendedMatch: "Erweitertes Match-Fenster",
      msgPackTitle: "Nachrichtenpakete",
      msgPackDesc: "Benötigst du heute mehr Nachrichten? Kaufe ein Einmalpaket ohne Planwechsel.",
      postPackTitle: "Beitragspakete",
      postPackDesc: "Teile heute mehr Fotos und Videos mit einem Einmalpaket.",
      callPackTitle: "Anrufpakete",
      callPackDesc: "Erhalte sofort zusätzliche Anrufzeit, verfügbar für Plus und höher.",
      packSmallMsg: "Klein — 2 Nachrichten",
      packMediumMsg: "Mittel — 5 Nachrichten",
      packLargeMsg: "Groß — 10 Nachrichten",
      packSmallPost: "Klein — 2 Beiträge",
      packMediumPost: "Mittel — 5 Beiträge",
      packLargePost: "Groß — 10 Beiträge",
      packSmallCall: "Klein — 5 Min",
      packMediumCall: "Mittel — 15 Min",
      packLargeCall: "Groß — 30 Min",
      packXLCall: "Extra Groß — Unbegrenzt (heute)",
      cmpDailyMsg: "Tägliche Nachrichten",
      cmpDailyPosts: "Tägliche Beiträge",
      cmpPush: "Push-Benachrichtigungen",
      cmpVoice: "Sprachanrufe",
      cmpVideo: "Videoanrufe",
      cmpTranslation: "Live-Übersetzung",
      cmpAdFree: "Werbefrei",
      cmpMatchWindow: "Erweitertes Match-Fenster",
      cmpPacks: "Verbrauchspakete",
    },
    contactModal: {
      title: "Kontaktiere uns",
      subtitle: "Alceylan Enterprises",
      nameLabel: "Name",
      emailLabel: "E-Mail",
      subjectLabel: "Betreff",
      messageLabel: "Nachricht",
      namePlaceholder: "Dein Name",
      emailPlaceholder: "deine@email.com",
      subjectPlaceholder: "Worum geht es?",
      messagePlaceholder: "Schreibe deine Nachricht hier...",
      sendButton: "Nachricht senden",
      sendingButton: "Wird gesendet...",
      closeButton: "Schließen",
      successTitle: "Nachricht gesendet!",
      successDesc: "Wir haben deine Nachricht erhalten und melden uns so bald wie möglich.",
      errorMessage: "Etwas ist schiefgelaufen. Bitte versuche es erneut oder schreibe uns direkt.",
      directEmail: "Oder schreibe uns direkt an",
    },
  },
  ja: {
    nav: { features: "特徴", howItWorks: "使い方", download: "ダウンロード" },
    hero: { title: "タイムラインが交差する場所", subtitle: "共有された過去と未来の夢を通じてマッチング。", cta: "アプリをダウンロード", tagline: "スワイプの終わり", translationBadge: "60ms ライブAI翻訳" },
    features: { title: "つながりの未来", subtitle: "スワイプにさよなら、深さにこんにちは。", card1: { title: "ストーリーベースのマッチング", desc: "共有された人生経験（投稿）と未来の夢（モーメント）でつながる。より深い会話はここから始まる。" }, card2: { title: "60msライブ翻訳", desc: "インスタントグローバル会話：超低遅延AIで、どこでも誰とでも、自分の言語でリアルタイムに話せます。" }, card3: { title: "管理されたメッセージング", desc: "マッチ → メッセージリクエスト → 承認。量より質。あなたの受信箱、あなたのルール。" }, phone1Title: "ストーリーベースのプロフィール", phone1Desc: "あなたの投稿があなたの旅を明かす", phone2Title: "体験を通じてつながる", phone2Desc: "同じ興味を持つ人を見つける", phone3Title: "モーメントを発見", phone3Desc: "周りの人の未来の計画を見る", ctaTitle: "タイムラインのマッチを見つける準備はできましたか？", ctaDesc: "共有された体験でつながる数千人に参加しましょう。", ctaButton: "無料で始める" },
    faq: { title: "よくある質問", q1: "Joinzyの違いは？", a1: "共有体験に基づいてマッチング。", q2: "投稿とモーメントとは？", a2: "投稿は過去。モーメントは未来。", q3: "メッセージングは？", a3: "受信箱はショーケース。", q4: "通話は？", a4: "最先端のミーティングルーム。", q5: "なぜJoinzy？", a5: "深く、表面的ではない。", stillHaveQuestions: "まだ質問がありますか？", contactUs: "お問い合わせ" },
    footer: { privacy: "プライバシー", terms: "利用規約", contact: "お問い合わせ", copyright: "© 2025 Alceylan Enterprises.", legal: "法的情報", tagline: "共有された体験と未来の夢で人々をつなぐ。意味のある繋がりのマトリックス。", subscription: "サブスクリプション", language: "言語", madeWith: "マトリックスで作られた" },
    bubbles: { post1: "東京タワー訪問", post2: "マディソンコンサート", moment1: "バリ旅行計画", moment2: "ハイキング仲間募集", post3: "カフェで出会い", moment3: "大晦日計画" },
    legal: { title: "法的情報とプライバシー", languageDisclaimer: "英語版が法的拘束力を持ちます。", footer: "全著作権所有。", lastUpdated: "最終更新", ageRestriction: { title: "年齢制限（18歳以上）", intro: "Joinzyは成人専用です。", warning: "18歳未満のデータは収集しません。", point1: "18歳以上必須", point2: "年齢確認が必要な場合があります", point3: "虚偽の年齢は違反です" }, locationData: { title: "位置情報とデータ", intro: "Joinzyは位置データを収集します。", geolocationTitle: "正確な位置情報", geolocationDesc: "マッチングとマトリックスマップに使用。", videoAudioTitle: "ライブビデオ/オーディオ", videoAudioDesc: "AI翻訳のためにリアルタイム処理。", privacyNote: "データは保存されません。", matrixTitle: "マトリックスマップ", matrixDesc: "匿名化された位置を表示。" }, payments: { title: "支払いと返金", intro: "すべての購入はポリシーに従います。", digitalGoods: "デジタル商品", digitalGoodsDesc: "すべての購入は即時デジタル商品です。", noRefund: "返金不可", noRefundDesc: "すべての販売は最終です。", refundRequest: "返金リクエスト", refundRequestDesc: "App StoreまたはPlay Storeへ。" }, safety: { title: "安全と行動", intro: "安全で敬意ある環境。", messageRequest: "メッセージリクエストシステム", messageRequestDesc: "承認が必要です。", zeroTolerance: "ゼロトレランス", harassment: "嫌がらせ", hateSpeech: "ヘイトスピーチ", illegalContent: "違法コンテンツ", impersonation: "なりすまし", spam: "スパム", consequences: "即時アカウント停止。" }, australian: { title: "オーストラリア法準拠", intro: "オーストラリア法を遵守。", privacyAct: "プライバシー法1988", privacyActDesc: "オーストラリアのプライバシー法を遵守。", apps: "APPs", appsDesc: "データの透明性。", gdpr: "GDPR", gdprDesc: "EEAユーザー向けコンプライアンス。" }, contact: { title: "お問い合わせ", intro: "ご質問はお問い合わせください。", emailLabel: "プライバシーに関するお問い合わせ", responseTime: "30日以内に回答。" } },
    cookies: { title: "プライバシー", description: "Cookieを使用しています。", acceptAll: "すべて受け入れる", rejectAll: "すべて拒否", customize: "カスタマイズ", settingsTitle: "Cookie設定", savePreferences: "保存", necessary: "必須", necessaryDesc: "機能に必要。", analytics: "分析", analyticsDesc: "インタラクションを理解。", marketing: "マーケティング", marketingDesc: "関連広告。", personalization: "パーソナライゼーション", personalizationDesc: "設定を記憶。" },
    subscription: {
      badge: "アプリ内サブスクリプションプラン",
      heroTitle: "あなたにぴったりの",
      heroPlan: "プランを見つけよう",
      heroDesc: "無料から無制限まで — あなたのつながり方に合ったプランをお選びください。すべての購入はJoinzyモバイルアプリで行われます。",
      billingNote: "利用可能な請求期間 — 価格はアプリで地域ごとに表示されます",
      mostPopular: "最も人気",
      purchaseInApp: "アプリで購入",
      comparisonTitle: "プラン比較",
      comparisonDesc: "各プランの内容を詳しく確認",
      featureLabel: "機能",
      addOnTitle: "アドオンパック",
      addOnDesc: "一回限りの購入 — サブスクリプション不要",
      partial: "一部",
      ctaTitle: "無料で始め、いつでもアップグレード",
      ctaDesc: "Joinzyアプリをダウンロードして接続を始めよう。Plus、Gold、Premiumへのアップグレードはアプリ内で直接できます。",
      ctaNote: "サブスクリプションは自動更新されます。App StoreまたはGoogle Playのアカウント設定からいつでもキャンセルできます。",
      downloadApp: "アプリをダウンロード",
      haveQuestions: "ご質問はありますか？",
      monthly: "月次",
      months3: "3ヶ月",
      months6: "6ヶ月",
      months12: "12ヶ月",
      popularTag: "人気",
      bestValueTag: "お得",
      freeDesc: "基本的な制限でJoinzyを無料でお試しください。",
      plusDesc: "より多くのチャット、少ない広告、毎日アクティブに。",
      goldDesc: "音声・ビデオ通話と完全な広告なし体験。",
      premiumDesc: "ライブ翻訳付き無制限ビデオ通話。",
      feat5msg: "1日5メッセージ",
      feat15msg: "1日15メッセージ",
      feat30msg: "1日30メッセージ",
      featUnlimitedMsg: "メッセージ無制限",
      feat3posts: "1日3投稿",
      feat7posts: "1日7投稿",
      feat15posts: "1日15投稿",
      featUnlimitedPosts: "投稿無制限",
      featPush: "プッシュ通知",
      featHashtag: "ハッシュタグマッチング",
      featMatrix: "マトリックス世界地図",
      featVoice: "音声通話",
      featVideo: "ビデオ通話",
      featTranslation: "ライブ翻訳",
      featReducedAds: "広告削減",
      featAdFree: "広告なし体験",
      featPacks: "メッセージ・通話パック",
      featCompleteAdFree: "完全広告なし",
      featExtendedMatch: "マッチ窓の延長",
      msgPackTitle: "メッセージパック",
      msgPackDesc: "今日もっとメッセージが必要？プランを変えずに一回限りのパックを購入。",
      postPackTitle: "投稿パック",
      postPackDesc: "一回限りの投稿パックで今日もっと写真・動画をシェア。",
      callPackTitle: "通話パック",
      callPackDesc: "すぐに追加通話時間を取得。Plus以上で利用可能。",
      packSmallMsg: "スモール — 2メッセージ",
      packMediumMsg: "ミディアム — 5メッセージ",
      packLargeMsg: "ラージ — 10メッセージ",
      packSmallPost: "スモール — 2投稿",
      packMediumPost: "ミディアム — 5投稿",
      packLargePost: "ラージ — 10投稿",
      packSmallCall: "スモール — 5分",
      packMediumCall: "ミディアム — 15分",
      packLargeCall: "ラージ — 30分",
      packXLCall: "エクストララージ — 無制限（本日）",
      cmpDailyMsg: "1日のメッセージ数",
      cmpDailyPosts: "1日の投稿数",
      cmpPush: "プッシュ通知",
      cmpVoice: "音声通話",
      cmpVideo: "ビデオ通話",
      cmpTranslation: "ライブ翻訳",
      cmpAdFree: "広告なし",
      cmpMatchWindow: "マッチ窓の延長",
      cmpPacks: "消耗パック",
    },
    contactModal: {
      title: "お問い合わせ",
      subtitle: "Alceylan Enterprises",
      nameLabel: "お名前",
      emailLabel: "メールアドレス",
      subjectLabel: "件名",
      messageLabel: "メッセージ",
      namePlaceholder: "お名前",
      emailPlaceholder: "your@email.com",
      subjectPlaceholder: "どのような件ですか？",
      messagePlaceholder: "メッセージをここに入力してください...",
      sendButton: "メッセージを送る",
      sendingButton: "送信中...",
      closeButton: "閉じる",
      successTitle: "送信完了！",
      successDesc: "メッセージを受け取りました。できるだけ早くご連絡します。",
      errorMessage: "エラーが発生しました。再試行するか、直接メールでお問い合わせください。",
      directEmail: "またはこちらへ直接メール",
    },
  },
  ko: {
    nav: { features: "기능", howItWorks: "사용 방법", download: "다운로드" },
    hero: { title: "타임라인이 교차하는 곳", subtitle: "공유된 과거와 미래의 꿈을 통해 매칭.", cta: "앱 다운로드", tagline: "스와이프의 끝", translationBadge: "60ms 실시간 AI 번역" },
    features: { title: "연결의 미래", subtitle: "스와이프에 작별을, 깊이에 안녕을.", card1: { title: "스토리 기반 매칭", desc: "공유된 삶의 경험(포스트)과 미래의 꿈(모멘트)을 통해 연결하세요. 더 깊은 대화는 여기서 시작됩니다." }, card2: { title: "60ms 실시간 번역", desc: "즉각적인 글로벌 대화: 초저지연 AI로 어디서나 누구와도 자신의 언어로 실시간으로 대화하세요." }, card3: { title: "관리된 메시징", desc: "매치 → 메시지 요청 → 승인. 양보다 질. 내 받은편지함, 내 규칙." }, phone1Title: "스토리 기반 프로필", phone1Desc: "내 포스트가 내 여정을 보여줍니다", phone2Title: "경험을 통한 연결", phone2Desc: "공통 관심사를 가진 사람 찾기", phone3Title: "모멘트 발견", phone3Desc: "주변 사람들의 미래 계획 보기", ctaTitle: "내 타임라인 매치를 찾을 준비가 되셨나요?", ctaDesc: "공유된 경험으로 연결되는 수천 명과 함께하세요.", ctaButton: "무료로 시작하기" },
    faq: { title: "자주 묻는 질문", q1: "Joinzy의 차이점은?", a1: "공유된 경험을 기반으로 매칭.", q2: "포스트와 모먼트란?", a2: "포스트는 과거. 모먼트는 미래.", q3: "메시징은?", a3: "받은 편지함은 쇼케이스.", q4: "통화는?", a4: "가장 진보된 회의실.", q5: "왜 Joinzy?", a5: "표면이 아닌 깊이.", stillHaveQuestions: "아직 질문이 있으신가요?", contactUs: "문의하기" },
    footer: { privacy: "개인정보", terms: "이용약관", contact: "문의", copyright: "© 2025 Alceylan Enterprises.", legal: "법적 정보", tagline: "공유된 경험과 미래의 꿈을 통해 사람들을 연결합니다. 의미 있는 연결의 매트릭스.", subscription: "구독", language: "언어", madeWith: "매트릭스로 만들어진" },
    bubbles: { post1: "도쿄 타워 방문", post2: "매디슨 콘서트", moment1: "발리 여행 계획", moment2: "하이킹 파트너 찾기", post3: "카페에서 만남", moment3: "새해 계획" },
    legal: { title: "법적 정보 및 개인정보", languageDisclaimer: "영어 버전이 법적 구속력을 갖습니다.", footer: "모든 권리 보유.", lastUpdated: "마지막 업데이트", ageRestriction: { title: "연령 제한 (18세 이상)", intro: "Joinzy는 성인 전용입니다.", warning: "18세 미만의 데이터는 수집하지 않습니다.", point1: "최소 18세 이상", point2: "연령 확인이 필요할 수 있습니다", point3: "허위 연령은 위반입니다" }, locationData: { title: "위치 및 데이터", intro: "Joinzy는 위치 데이터를 수집합니다.", geolocationTitle: "정확한 위치", geolocationDesc: "매칭과 매트릭스 맵에 사용.", videoAudioTitle: "라이브 비디오/오디오", videoAudioDesc: "AI 번역을 위한 실시간 처리.", privacyNote: "데이터는 저장되지 않습니다.", matrixTitle: "매트릭스 맵", matrixDesc: "익명화된 위치 표시." }, payments: { title: "결제 및 환불", intro: "모든 구매는 정책을 따릅니다.", digitalGoods: "디지털 상품", digitalGoodsDesc: "모든 구매는 즉시 디지털 상품입니다.", noRefund: "환불 불가", noRefundDesc: "모든 판매는 최종입니다.", refundRequest: "환불 요청", refundRequestDesc: "App Store 또는 Play Store로." }, safety: { title: "안전 및 행동", intro: "안전하고 존중하는 환경.", messageRequest: "메시지 요청 시스템", messageRequestDesc: "승인이 필요합니다.", zeroTolerance: "무관용", harassment: "괴롭힘", hateSpeech: "혐오 발언", illegalContent: "불법 콘텐츠", impersonation: "사칭", spam: "스팸", consequences: "즉시 계정 정지." }, australian: { title: "호주 법률 준수", intro: "호주 법률을 준수합니다.", privacyAct: "개인정보보호법 1988", privacyActDesc: "호주 개인정보보호법 준수.", apps: "APPs", appsDesc: "데이터 투명성.", gdpr: "GDPR", gdprDesc: "EEA 사용자를 위한 준수." }, contact: { title: "문의", intro: "질문이 있으시면 연락주세요.", emailLabel: "개인정보 문의", responseTime: "30일 이내 답변." } },
    cookies: { title: "개인정보", description: "쿠키를 사용합니다.", acceptAll: "모두 수락", rejectAll: "모두 거부", customize: "사용자 정의", settingsTitle: "쿠키 설정", savePreferences: "저장", necessary: "필수", necessaryDesc: "기능에 필요.", analytics: "분석", analyticsDesc: "상호작용 이해.", marketing: "마케팅", marketingDesc: "관련 광고.", personalization: "개인화", personalizationDesc: "설정 기억." },
    subscription: {
      badge: "앱 내 구독 플랜",
      heroTitle: "나에게 딱 맞는",
      heroPlan: "플랜 찾기",
      heroDesc: "무료부터 무제한까지 — 나의 소통 방식에 맞는 플랜을 선택하세요. 모든 구매는 Joinzy 모바일 앱에서 이루어집니다.",
      billingNote: "이용 가능한 청구 기간 — 가격은 앱에서 지역에 따라 표시됩니다",
      mostPopular: "가장 인기",
      purchaseInApp: "앱에서 구매",
      comparisonTitle: "플랜 비교",
      comparisonDesc: "각 플랜에 포함된 내용을 정확히 확인하세요",
      featureLabel: "기능",
      addOnTitle: "추가 팩",
      addOnDesc: "일회성 구매 — 구독 불필요",
      partial: "부분",
      ctaTitle: "무료로 시작하고 언제든지 업그레이드",
      ctaDesc: "Joinzy 앱을 다운로드하고 연결을 시작하세요. Plus, Gold 또는 Premium으로 앱에서 직접 업그레이드하세요.",
      ctaNote: "구독은 자동 갱신됩니다. App Store 또는 Google Play 계정 설정에서 언제든지 취소할 수 있습니다.",
      downloadApp: "앱 다운로드",
      haveQuestions: "질문이 있으신가요?",
      monthly: "월간",
      months3: "3개월",
      months6: "6개월",
      months12: "12개월",
      popularTag: "인기",
      bestValueTag: "최고 가성비",
      freeDesc: "기본 제한으로 Joinzy를 무료로 체험하세요.",
      plusDesc: "더 많은 채팅, 적은 광고, 매일 활발하게.",
      goldDesc: "음성 및 화상 통화와 완전한 광고 없는 경험.",
      premiumDesc: "실시간 번역이 포함된 무제한 화상 통화.",
      feat5msg: "하루 5개 메시지",
      feat15msg: "하루 15개 메시지",
      feat30msg: "하루 30개 메시지",
      featUnlimitedMsg: "무제한 메시지",
      feat3posts: "하루 3개 게시물",
      feat7posts: "하루 7개 게시물",
      feat15posts: "하루 15개 게시물",
      featUnlimitedPosts: "무제한 게시물",
      featPush: "푸시 알림",
      featHashtag: "해시태그 매칭",
      featMatrix: "매트릭스 세계 지도",
      featVoice: "음성 통화",
      featVideo: "화상 통화",
      featTranslation: "실시간 번역",
      featReducedAds: "광고 감소",
      featAdFree: "광고 없는 경험",
      featPacks: "메시지 및 통화 팩",
      featCompleteAdFree: "완전한 광고 없음",
      featExtendedMatch: "확장된 매치 창",
      msgPackTitle: "메시지 팩",
      msgPackDesc: "오늘 더 많은 메시지가 필요하세요? 플랜 변경 없이 일회성 팩을 구매하세요.",
      postPackTitle: "게시물 팩",
      postPackDesc: "일회성 게시물 팩으로 오늘 더 많은 사진과 동영상을 공유하세요.",
      callPackTitle: "통화 팩",
      callPackDesc: "즉시 추가 통화 시간을 얻으세요. Plus 이상에서 이용 가능합니다.",
      packSmallMsg: "스몰 — 2개 메시지",
      packMediumMsg: "미디엄 — 5개 메시지",
      packLargeMsg: "라지 — 10개 메시지",
      packSmallPost: "스몰 — 2개 게시물",
      packMediumPost: "미디엄 — 5개 게시물",
      packLargePost: "라지 — 10개 게시물",
      packSmallCall: "스몰 — 5분",
      packMediumCall: "미디엄 — 15분",
      packLargeCall: "라지 — 30분",
      packXLCall: "엑스트라 라지 — 무제한 (오늘)",
      cmpDailyMsg: "일일 메시지",
      cmpDailyPosts: "일일 게시물",
      cmpPush: "푸시 알림",
      cmpVoice: "음성 통화",
      cmpVideo: "화상 통화",
      cmpTranslation: "실시간 번역",
      cmpAdFree: "광고 없음",
      cmpMatchWindow: "확장된 매치 창",
      cmpPacks: "소모성 팩",
    },
    contactModal: {
      title: "문의하기",
      subtitle: "Alceylan Enterprises",
      nameLabel: "이름",
      emailLabel: "이메일",
      subjectLabel: "제목",
      messageLabel: "메시지",
      namePlaceholder: "이름을 입력하세요",
      emailPlaceholder: "your@email.com",
      subjectPlaceholder: "어떤 내용인가요?",
      messagePlaceholder: "메시지를 입력하세요...",
      sendButton: "메시지 보내기",
      sendingButton: "전송 중...",
      closeButton: "닫기",
      successTitle: "메시지 전송 완료!",
      successDesc: "메시지를 받았습니다. 최대한 빨리 답변 드리겠습니다.",
      errorMessage: "문제가 발생했습니다. 다시 시도하거나 직접 이메일을 보내주세요.",
      directEmail: "또는 직접 이메일 보내기",
    },
  },
  zh: {
    nav: { features: "功能", howItWorks: "使用方法", download: "下载" },
    hero: { title: "时间线交汇之处", subtitle: "通过共同的过去和未来的梦想进行匹配。", cta: "下载应用", tagline: "刷卡的终结", translationBadge: "60ms 实时AI翻译" },
    features: { title: "连接的未来", subtitle: "告别滑动，迎接深度。", card1: { title: "基于故事的匹配", desc: "通过共享的人生经历（帖子）和未来梦想（时刻）建立联系。更深入的对话从这里开始。" }, card2: { title: "60毫秒实时翻译", desc: "即时全球对话：通过超低延迟AI，随时随地用自己的语言与任何人实时交流。" }, card3: { title: "受控消息传递", desc: "匹配 → 消息请求 → 批准。质量胜于数量。您的收件箱，您的规则。" }, phone1Title: "基于故事的个人资料", phone1Desc: "您的帖子揭示您的旅程", phone2Title: "通过体验建立联系", phone2Desc: "找到与您兴趣相同的人", phone3Title: "发现时刻", phone3Desc: "查看周围人的未来计划", ctaTitle: "准备好找到您的时间线匹配了吗？", ctaDesc: "加入数千人通过共享经历建立联系的行列。", ctaButton: "免费开始" },
    faq: { title: "常见问题", q1: "Joinzy有何不同？", a1: "基于共同经历进行匹配。", q2: "什么是帖子和时刻？", a2: "帖子是过去。时刻是未来。", q3: "消息系统？", a3: "收件箱是展示窗。", q4: "通话？", a4: "最先进的会议室。", q5: "为什么选择Joinzy？", a5: "深入，而非表面。", stillHaveQuestions: "还有问题吗？", contactUs: "联系我们" },
    footer: { privacy: "隐私政策", terms: "条款", contact: "联系", copyright: "© 2025 Alceylan Enterprises.", legal: "法律信息", tagline: "通过共享的经历和未来梦想连接人们。有意义联系的矩阵。", subscription: "订阅", language: "语言", madeWith: "用矩阵制作" },
    bubbles: { post1: "东京塔访问", post2: "麦迪逊音乐会", moment1: "巴厘岛旅行计划", moment2: "寻找徒步伙伴", post3: "咖啡馆相遇", moment3: "新年计划" },
    legal: { title: "法律和隐私", languageDisclaimer: "英文版具有法律约束力。", footer: "保留所有权利。", lastUpdated: "最后更新", ageRestriction: { title: "年龄限制（18岁以上）", intro: "Joinzy仅供成人使用。", warning: "我们不收集18岁以下的数据。", point1: "必须年满18岁", point2: "可能需要年龄验证", point3: "虚假年龄是违规行为" }, locationData: { title: "位置和数据", intro: "Joinzy收集位置数据。", geolocationTitle: "精确位置", geolocationDesc: "用于匹配和矩阵地图。", videoAudioTitle: "实时视频/音频", videoAudioDesc: "AI翻译的实时处理。", privacyNote: "数据永不存储。", matrixTitle: "矩阵地图", matrixDesc: "显示匿名位置。" }, payments: { title: "支付和退款", intro: "所有购买遵循政策。", digitalGoods: "数字商品", digitalGoodsDesc: "所有购买都是即时数字商品。", noRefund: "不退款", noRefundDesc: "所有销售都是最终的。", refundRequest: "退款请求", refundRequestDesc: "请联系App Store或Play Store。" }, safety: { title: "安全和行为", intro: "安全尊重的环境。", messageRequest: "消息请求系统", messageRequestDesc: "需要批准。", zeroTolerance: "零容忍", harassment: "骚扰", hateSpeech: "仇恨言论", illegalContent: "非法内容", impersonation: "冒充", spam: "垃圾邮件", consequences: "立即账户停止。" }, australian: { title: "澳大利亚法律合规", intro: "我们遵守澳大利亚法律。", privacyAct: "隐私法1988", privacyActDesc: "遵守澳大利亚隐私法。", apps: "APPs", appsDesc: "数据透明度。", gdpr: "GDPR", gdprDesc: "EEA用户合规。" }, contact: { title: "联系我们", intro: "如有问题请联系我们。", emailLabel: "隐私咨询", responseTime: "30天内回复。" } },
    cookies: { title: "隐私", description: "我们使用Cookie。", acceptAll: "全部接受", rejectAll: "全部拒绝", customize: "自定义", settingsTitle: "Cookie设置", savePreferences: "保存", necessary: "必要", necessaryDesc: "功能所需。", analytics: "分析", analyticsDesc: "了解互动。", marketing: "营销", marketingDesc: "相关广告。", personalization: "个性化", personalizationDesc: "记住设置。" },
    subscription: {
      badge: "应用内订阅计划",
      heroTitle: "找到您的完美",
      heroPlan: "计划",
      heroDesc: "从免费到无限 — 选择适合您连接方式的计划。所有购买均在Joinzy移动应用中进行。",
      billingNote: "可用计费周期 — 价格根据您的地区在应用中显示",
      mostPopular: "最受欢迎",
      purchaseInApp: "在应用中购买",
      comparisonTitle: "计划对比",
      comparisonDesc: "查看每个计划中具体包含的内容",
      featureLabel: "功能",
      addOnTitle: "附加包",
      addOnDesc: "一次性购买 — 无需订阅",
      partial: "部分",
      ctaTitle: "免费开始，随时升级",
      ctaDesc: "下载Joinzy应用并开始连接。通过App Store或Google Play直接在应用中升级到Plus、Gold或Premium。",
      ctaNote: "订阅自动续订。随时从App Store或Google Play账户设置中取消。",
      downloadApp: "下载应用",
      haveQuestions: "有问题吗？",
      monthly: "月度",
      months3: "3个月",
      months6: "6个月",
      months12: "12个月",
      popularTag: "热门",
      bestValueTag: "最超值",
      freeDesc: "免费体验Joinzy的基本功能。",
      plusDesc: "更多聊天，更少广告，每天保持活跃。",
      goldDesc: "语音和视频通话，完全无广告体验。",
      premiumDesc: "无限视频通话，含实时翻译。",
      feat5msg: "每天5条消息",
      feat15msg: "每天15条消息",
      feat30msg: "每天30条消息",
      featUnlimitedMsg: "无限消息",
      feat3posts: "每天3篇帖子",
      feat7posts: "每天7篇帖子",
      feat15posts: "每天15篇帖子",
      featUnlimitedPosts: "无限帖子",
      featPush: "推送通知",
      featHashtag: "标签匹配",
      featMatrix: "矩阵世界地图",
      featVoice: "语音通话",
      featVideo: "视频通话",
      featTranslation: "实时翻译",
      featReducedAds: "减少广告",
      featAdFree: "无广告体验",
      featPacks: "消息和通话包",
      featCompleteAdFree: "完全无广告",
      featExtendedMatch: "扩展匹配窗口",
      msgPackTitle: "消息包",
      msgPackDesc: "今天需要更多消息？无需更改计划即可购买一次性包。",
      postPackTitle: "帖子包",
      postPackDesc: "使用一次性帖子包今天分享更多照片和视频。",
      callPackTitle: "通话包",
      callPackDesc: "立即获得额外通话时间，适用于Plus及以上。",
      packSmallMsg: "小 — 2条消息",
      packMediumMsg: "中 — 5条消息",
      packLargeMsg: "大 — 10条消息",
      packSmallPost: "小 — 2篇帖子",
      packMediumPost: "中 — 5篇帖子",
      packLargePost: "大 — 10篇帖子",
      packSmallCall: "小 — 5分钟",
      packMediumCall: "中 — 15分钟",
      packLargeCall: "大 — 30分钟",
      packXLCall: "特大 — 无限（今天）",
      cmpDailyMsg: "每日消息",
      cmpDailyPosts: "每日帖子",
      cmpPush: "推送通知",
      cmpVoice: "语音通话",
      cmpVideo: "视频通话",
      cmpTranslation: "实时翻译",
      cmpAdFree: "无广告",
      cmpMatchWindow: "扩展匹配窗口",
      cmpPacks: "消耗包",
    },
    contactModal: {
      title: "联系我们",
      subtitle: "Alceylan Enterprises",
      nameLabel: "姓名",
      emailLabel: "电子邮件",
      subjectLabel: "主题",
      messageLabel: "消息",
      namePlaceholder: "您的姓名",
      emailPlaceholder: "your@email.com",
      subjectPlaceholder: "这是关于什么的？",
      messagePlaceholder: "在此处输入您的消息...",
      sendButton: "发送消息",
      sendingButton: "发送中...",
      closeButton: "关闭",
      successTitle: "消息已发送！",
      successDesc: "我们已收到您的消息，将尽快回复您。",
      errorMessage: "出现了错误。请重试或直接发送电子邮件给我们。",
      directEmail: "或直接发送电子邮件至",
    },
  },
};
